import { useCallback, useState } from 'react';
import { Button, Text, Textarea, Tooltip, makeStyles, tokens } from '@coreai/fluentui-react';
import {
  ThumbDislikeFilled,
  ThumbDislikeRegular,
  ThumbLikeFilled,
  ThumbLikeRegular,
} from '@fluentui/react-icons';
import { useSubmitAssistantFeedback } from '@/hooks/useSubmitAssistantFeedback';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import type { FeedbackSentiment } from '@/services/workflowAssistantFeedback';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  prompt: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  commentRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalXS,
  },
  textarea: {
    flex: 1,
  },
});

interface ProposalFeedbackProps {
  /**
   * A previously-submitted sentiment for this turn (from chat state / reloaded
   * history). When set, only the thumbs render (read-only, chosen one
   * highlighted) — the comment box and Send are gone.
   */
  submittedSentiment?: FeedbackSentiment | null;
}

/**
 * Thumbs up/down feedback control shown under the assistant's completed reply.
 * Clicking a thumb selects a sentiment and reveals an optional comment box;
 * "Send" submits once (fire-and-forget) and records the sentiment on the turn.
 * After submit the comment box and Send disappear, leaving just the highlighted
 * thumb — persisted so a reloaded chat shows the same rating.
 */
export const ProposalFeedback = ({ submittedSentiment }: ProposalFeedbackProps) => {
  const styles = useStyles();
  const submitFeedback = useSubmitAssistantFeedback();
  const setLatestAssistantFeedback = useAgentChatAguiStore((s) => s.setLatestAssistantFeedback);
  const [sentiment, setSentiment] = useState<FeedbackSentiment | null>(null);
  const [comment, setComment] = useState('');

  const handleSend = useCallback(() => {
    if (!sentiment) {
      return;
    }
    // Record on the turn first (optimistic → persists + collapses to the
    // read-only thumb), then fire the (fire-and-forget) backend submission.
    setLatestAssistantFeedback(sentiment);
    void submitFeedback(sentiment, comment.trim() || undefined);
  }, [sentiment, comment, submitFeedback, setLatestAssistantFeedback]);

  // Already submitted (this session or a reloaded chat) → keep the same line,
  // just read-only with the chosen thumb highlighted. No comment box / Send.
  if (submittedSentiment) {
    const isUp = submittedSentiment === 'ThumbsUp';
    return (
      <div className={styles.prompt} role="group" aria-label="Submitted feedback">
        <Text size={200}>Was this helpful?</Text>
        <Button
          appearance={isUp ? 'primary' : 'subtle'}
          size="small"
          icon={isUp ? <ThumbLikeFilled /> : <ThumbLikeRegular />}
          aria-label="Thumbs up"
          aria-pressed={isUp}
          disabled
        />
        <Button
          appearance={!isUp ? 'primary' : 'subtle'}
          size="small"
          icon={!isUp ? <ThumbDislikeFilled /> : <ThumbDislikeRegular />}
          aria-label="Thumbs down"
          aria-pressed={!isUp}
          disabled
        />
      </div>
    );
  }

  return (
    <div className={styles.root} role="group" aria-label="Rate this response">
      <div className={styles.prompt}>
        <Text size={200}>Was this helpful?</Text>
        <Tooltip content="Good result" relationship="label">
          <Button
            appearance={sentiment === 'ThumbsUp' ? 'primary' : 'subtle'}
            size="small"
            icon={sentiment === 'ThumbsUp' ? <ThumbLikeFilled /> : <ThumbLikeRegular />}
            aria-label="Thumbs up"
            aria-pressed={sentiment === 'ThumbsUp'}
            onClick={() => setSentiment('ThumbsUp')}
          />
        </Tooltip>
        <Tooltip content="Needs work" relationship="label">
          <Button
            appearance={sentiment === 'ThumbsDown' ? 'primary' : 'subtle'}
            size="small"
            icon={sentiment === 'ThumbsDown' ? <ThumbDislikeFilled /> : <ThumbDislikeRegular />}
            aria-label="Thumbs down"
            aria-pressed={sentiment === 'ThumbsDown'}
            onClick={() => setSentiment('ThumbsDown')}
          />
        </Tooltip>
      </div>

      {sentiment && (
        <div className={styles.commentRow}>
          <Textarea
            className={styles.textarea}
            size="small"
            resize="vertical"
            value={comment}
            onChange={(_e, data) => setComment(data.value)}
            placeholder="Add a comment (optional)"
            aria-label="Feedback comment"
          />
          <Button appearance="primary" size="small" onClick={handleSend}>
            Send
          </Button>
        </div>
      )}
    </div>
  );
};
